import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHelmet } from '@agro-club/frontend-shared'
// eslint-disable-next-line prettier/prettier
import type { UploadProps } from 'antd'
import { Button, Divider, Flex, Form, Input, Upload, UploadFile, message } from 'antd'
import { FormFields } from './DigitalAssetDetailsForm/types'
import { InboxOutlined } from '@ant-design/icons'
import { DigitalAssetDocumentType } from '../../../types/entities'
import { assetNameCharacterLimit, descriptionCharacterLimit } from './types'

const { Dragger } = Upload
const { TextArea } = Input

// List of supported file extensions (lowercase)
const SUPPORTED_EXTENSIONS = Object.values(DigitalAssetDocumentType).map((type) => type.toLowerCase())

// Helper function to check if a file type is supported
const isFileTypeSupported = (fileName: string): boolean => {
  const ext = fileName.split('.').pop()?.toLowerCase()
  if (!ext) {
    return false
  }
  return SUPPORTED_EXTENSIONS.includes(ext)
}

type PropsType = {
  cancelCallback: () => void
  submitCallback: (formFields: FormFields) => void
}

const DigitalAssetDocumentForm: React.FC<PropsType> = ({ cancelCallback, submitCallback }: PropsType) => {
  const { t } = useTranslation(['digitalAsset', 'common'])
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [form] = Form.useForm<FormFields>()

  const addFile = (file: UploadFile) => {
    // Validate file type before adding
    if (!isFileTypeSupported(file.name || '')) {
      const supportedTypes = Object.values(DigitalAssetDocumentType).join(', ')
      message.error(
        t('form.rules.unsupportedFileType', {
          fileName: file.name,
          supportedTypes,
          defaultValue: `File type not supported. Supported types: ${supportedTypes}`,
        }),
      )
      return Upload.LIST_IGNORE
    }
    setFileList([...fileList, file])
    return false
  }

  const getFile = (e: { file: { status: string } }) => {
    if (e.file.status === 'removed') {
      return undefined
    }
    return e.file
  }

  const uploadProps: UploadProps = {
    onRemove: (file: UploadFile) => removeFromUploader(file),
    name: 'file',
    beforeUpload: (file: UploadFile) => addFile(file),
    fileList,
  }

  const removeFromUploader = (file: UploadFile) => {
    const index = fileList.indexOf(file)
    const newFileList = fileList.slice()
    newFileList.splice(index, 1)
    setFileList(newFileList)
  }

  useHelmet({ title: t('addDigitalAssetMetaTitle') })

  return (
    <Form form={form} onFinish={submitCallback} layout="vertical">
      <Form.Item
        name="file"
        rules={[
          {
            required: true,
            message: t('form.rules.missingFile'),
          },
          {
            validator: (_, value) => {
              if (!value) {
                return Promise.resolve()
              }
              const fileName = value.name || ''
              if (!isFileTypeSupported(fileName)) {
                const supportedTypes = Object.values(DigitalAssetDocumentType).join(', ')
                return Promise.reject(
                  new Error(
                    t('form.rules.unsupportedFileType', {
                      supportedTypes,
                      defaultValue: `File type not supported.`,
                    }),
                  ),
                )
              }
              return Promise.resolve()
            },
          },
        ]}
        getValueFromEvent={getFile}
      >
        <Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">{t('form.uploadTitle')}</p>
          <p className="ant-upload-hint">
            `{t('form.uploadHint')} {Object.values(DigitalAssetDocumentType).join(', ')}`
          </p>
        </Dragger>
      </Form.Item>
      <Form.Item
        name="asset_name"
        rules={[
          {
            required: true,
            message: t('form.rules.missingAssetName'),
          },
          {
            max: assetNameCharacterLimit,
            message: `${assetNameCharacterLimit} ${t('form.rules.assetNameLimitReached')}`,
          },
        ]}
        label={t('form.assetName')}
      >
        <TextArea
          rows={3}
          count={{
            max: assetNameCharacterLimit,
            show: true,
          }}
        />
      </Form.Item>

      <Form.Item
        name="description"
        rules={[
          {
            required: true,
            message: t('form.rules.missingDescription'),
          },
          {
            max: descriptionCharacterLimit,
            message: `${descriptionCharacterLimit} ${t('form.rules.descriptionLimitReached')}`,
          },
        ]}
        label={t('form.documentDescription')}
      >
        <TextArea
          rows={6}
          count={{
            max: descriptionCharacterLimit,
            show: true,
          }}
        />
      </Form.Item>
      <Divider />
      <Form.Item>
        <Flex justify="flex-end" gap="middle">
          <Button type="default" onClick={cancelCallback}>
            {t('form.cancelButton')}
          </Button>
          <Button type="primary" htmlType="submit">
            {t('form.continueButton')}
          </Button>
        </Flex>
      </Form.Item>
    </Form>
  )
}

export default DigitalAssetDocumentForm
